import { createAndUpdateSecret, deleteSecret } from "../../BackendFacade/apiCalls";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useMain } from "../../../../Utils/SessionControls/mainContext";
import React from "react";
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxInputField from "../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import { ISecret, IRepo, animatedComponents } from "../../Interfaces/IGithub";
import { HubPathRouting } from "../../../../HubFramework/pathBuilder";
import Select from 'react-select';
import { colorStyles, slugStyle } from "../Styles/styles";

interface Props {
    secret?: ISecret | null;
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => Promise<void> | void;
    org: string;
    slug: string;
    allRepos: IRepo[];
    id?: string;
}

const SecretSidesheet: React.FC<Props> = ({ secret, isOpen, onClose, onSaved, org, slug, allRepos, id }) => {
    const creating = !secret;
    const [name, setName] = useState('');
    const [secretValue, setSecretValue] = useState('');
    const [selectedRepos, setSelectedRepos] = useState<IRepo[]>([]);
    const [deleting, setDeleting] = useState(false);
    const { setLoading, createToast } = useMain();
    const secretValueTextareaRef = useRef<any>(null);

    useEffect(() => {
        setName(secret?.name ? secret.name.replace(`${slug}_`, '') : '');
        setSelectedRepos(secret?.selectedRepos || []);
        setSecretValue('');
    }, [secret, isOpen, slug]);

    useEffect(() => {
        if (secretValueTextareaRef.current) {
            secretValueTextareaRef.current.autoResize = true;
        }
    }, [isOpen]);

    const handleRepoChange = useCallback((selected) => {
        setSelectedRepos(selected ? selected.map(
            opt => allRepos.find(r => r.id.toString() === opt.value) || { id: Number(opt.value), name: opt.label, updated_at: "" }
        ) : []);
    }, [allRepos]);

    const save = useCallback(async () => {
        setLoading(true);
        try {
            const repoIDs = selectedRepos.map(r => r.id.toString());
            const finalName = `${slug}_${name.trim()}`;
            await createAndUpdateSecret(
                HubPathRouting.currentProject.id,
                HubPathRouting.currentModule.id,
                {
                    secretName: finalName,
                    secretValue: creating ? secretValue : '',
                    repoIDs
                }
            );
            createToast({ message: creating ? "Secret created" : "Secret updated", variant: "success", title: "Success" });
            onClose();
            await onSaved();
        } catch (e) {
            createToast({ message: e instanceof Error ? e.message : String(e), variant: "danger", title: "Error" });
        } finally {
            setLoading(false);
        }
    }, [creating, selectedRepos, name, secretValue, onClose, onSaved, setLoading, createToast, slug]);

    const doDelete = useCallback(async () => {
        if (!secret) return;
        if (!window.confirm(`Delete secret "${secret.name}"? This cannot be undone.`)) return;
        setDeleting(true);
        setLoading(true);
        try {
            await deleteSecret(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, secret.name);
            createToast({ message: "Secret deleted", variant: "success", title: "Deleted" });
            onClose();
            await onSaved();
        } catch (e) {
            createToast({ message: e instanceof Error ? e.message : String(e), variant: "danger", title: "Error" });
        } finally {
            setDeleting(false);
            setLoading(false);
        }
    }, [secret, setLoading, createToast, onClose, onSaved]);

    const footer = useMemo(() => [
        !creating && (
            <odx-button
                id="secret-sidesheet-delete-btn"
                key="delete"
                variant="danger"
                size="lg"
                type="button"
                onClick={doDelete}
                disabled={deleting ? true : undefined}
            >
                Delete
            </odx-button>
        ),
        <odx-button
            id="secret-sidesheet-cancel-btn"
            key="cancel"
            variant="ghost"
            size="lg"
            type="button"
            onClick={onClose}
        >
            Cancel
        </odx-button>,
        <odx-button
            id="secret-sidesheet-save-btn"
            key="save"
            variant="primary"
            size="lg"
            type="button"
            onClick={save}
        >
            Save
        </odx-button>
    ].filter(Boolean), [creating, doDelete, deleting, onClose, save]);

    const repoOptions = useMemo(() => allRepos.map(r => ({ value: r.id.toString(), label: r.name })), [allRepos]);
    const selectedRepoValues = useMemo(() => selectedRepos.map(r => ({ value: r.id.toString(), label: r.name })), [selectedRepos]);

    return (
        <OdxModal
            size="small"
            variant="sidesheet"
            title={creating ? 'Add secret' : `Edit ${secret?.name}`}
            isOpen={isOpen}
            onClose={onClose}
            footerButtons={footer}
            id={id ? `${id}-modal${secret ? '-' + secret.name : ''}` : `secret-sidesheet-modal${secret ? '-' + secret?.name : ''}`}
        >
            <div data-odxLayout="vertical" id={id ? `${id}-vertical-layout` : "secret-sidesheet-vertical-layout"}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: creating ? undefined : '1em' }}>
                    <span style={slugStyle} id={id ? `${id}_slug` : "secret-sidesheet-slug"}>
                        {slug}_
                    </span>
                    <OdxInputField
                        label="Name"
                        id={id ? `${id}-name-input` : "secret-name-input"}
                        value={name}
                        onChange={e => setName(e.target.value.toUpperCase())}
                        isRequired={true}
                        className="flex-grow-1"
                        placeholder={"May only contain lettes, numbers and dashes"}
                        style={{ borderRadius: '0 4px 4px 0', borderLeft: 'none' }}
                    />
                </div>
                <div
                    className={`odx-form-field odx-form-field--full ${creating ? 'odx-form-field--required' : ''}`}
                    id={id ? `${id}-secret-value-field` : 'secret-value-field'}
                >
                    <label
                        className="odx-form-field__label"
                        htmlFor={id ? `${id}-value-textarea` : 'secret-value-textarea'}
                        aria-owns={id ? `${id}-value-textarea` : 'secret-value-textarea'}
                    >
                        <span className="odx-form-field-label">Secret Value</span>
                        {creating && <span className="odx-form-field__label-is-required">*</span>}
                    </label>
                    <div className="odx-form-field__inner">
                        <div className="odx-form-field__control">
                            <odx-textarea
                                ref={secretValueTextareaRef}
                                rows={6}
                                value={secretValue}
                                placeholder={
                                    !creating
                                        ? "Override value, leave empty to keep previous value"
                                        : undefined
                                }
                                id={id ? `${id}-value-textarea` : 'secret-value-textarea'}
                                className={`secret-textarea odx-fill ${creating ? 'required' : ''}`}
                                onInput={event => {
                                    const target = event.target as HTMLTextAreaElement;
                                    setSecretValue(target.value);
                                }}
                                style={
                                    {
                                        '--control-max-height': '24rem'
                                    } as React.CSSProperties
                                }
                            />
                        </div>
                    </div>
                </div>
                <div className="form-group" id={id ? `${id}-linked-repos-group` : "secret-linked-repos-group"}>
                    <label id={id ? `${id}-linked-repos-label` : "secret-linked-repos-label"}>Linked Repositories</label>
                    <Select
                        closeMenuOnSelect={false}
                        isMulti
                        options={repoOptions}
                        value={selectedRepoValues}
                        onChange={handleRepoChange}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        styles={colorStyles}
                        id={id ? `${id}-linked-repos-select` : "secret-linked-repos-select"}
                    />
                </div>
            </div>
        </OdxModal>
    );
};

export default SecretSidesheet;
